import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button, Checkbox, LoadingBlock, Modal, toast } from "@/components/ds";

/**
 * The disclosure a rep reads aloud before taking payment.
 *
 * A refund policy stops refunds; it does not stop a chargeback, where the bank takes the money back
 * months later while it is argued. What settles that argument is evidence the customer was told
 * what they were buying before they paid. So the words are shown exactly as they must be read, and
 * ticking the box records who read them, when, and which version — against this sale.
 */
export function DisclosureModal({ accountId, businessName, open, onOpenChange }: {
  accountId: string | null;
  businessName?: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["onboarding-disclosure", accountId],
    queryFn: () => api.onboardingDisclosure(accountId as string),
    enabled: open && !!accountId,
  });

  const ackMut = useMutation({
    mutationFn: () => api.onboardingAcknowledge(accountId as string),
    onSuccess: () => {
      toast.success("Recorded. The customer can now pay.");
      qc.invalidateQueries({ queryKey: ["onboarding-accounts"] });
      qc.invalidateQueries({ queryKey: ["onboarding-disclosure", accountId] });
      setConfirmed(false);
      onOpenChange(false);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const already = data?.acknowledged;

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Read this before taking payment"
      description={businessName ? `For ${businessName}` : undefined} testid="disclosure-modal">
      {isLoading || !data ? <LoadingBlock rows={4} /> : (
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed whitespace-pre-wrap text-slate-800"
            data-testid="disclosure-text">
            {data.text}
          </div>

          {already ? (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
              data-testid="disclosure-done">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                Read by {data.acknowledgement?.acknowledged_by_name} on{" "}
                {new Date(data.acknowledgement?.acknowledged_at).toLocaleString()} (version {data.acknowledgement?.version}).
                The customer can pay.
              </div>
            </div>
          ) : (
            <>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <Checkbox checked={confirmed} onChange={setConfirmed} testid="disclosure-confirm" />
                <span>I read this to the customer and they said they understood and agreed.</span>
              </label>
              <p className="text-xs text-slate-400">
                This is recorded against the sale with your name and the time. Payment cannot be taken until it is.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button onClick={() => ackMut.mutate()} disabled={!confirmed || ackMut.isPending} data-testid="disclosure-submit">
                  {ackMut.isPending ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Recording…</> : "Record and unlock payment"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
